import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../shared/alert/shared/alert.service';
import { AlertTypes } from '../../shared/alert/shared/alert-types';
import { LoginService } from '../shared/services/login.service';
import { JwtTokenService } from '../../core/shared/jwt-token.service';
import { UserDataService } from '../shared/services/user-data.service';
import { LoginResponse } from '../shared/interfaces/two-factor.interface';
import { UserDataResponse } from '../shared/interfaces/login-data-response.interface';

/**
 * Public page behind the link in the API's account emails
 * ({PortalBaseUrl}/account/reset-password?token=...). Covers both cases:
 * initial password after user creation and new password after "forgot password".
 * After the password is set the user is logged in directly; a pending 2FA step is handed over to the
 * login page via router state.
 */
@Component({
  selector: 'avs-account-reset-password',
  templateUrl: './account-reset-password.component.html',
  styleUrls: ['./account-reset-password.component.css']
})
export class AccountResetPasswordComponent implements OnInit {
  public step: 'validating' | 'form' | 'invalid' = 'validating';
  /** Confirmed password from avs-password-confirm-input, or '' while the inputs do not match. */
  public password: string = '';
  public isLoading: boolean = false;

  private token: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loginService: LoginService,
    private alertService: AlertService,
    private jwtTokenService: JwtTokenService,
    private userDataService: UserDataService
  ) {
  }

  public ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';

    if (!this.token) {
      this.step = 'invalid';
      return;
    }

    this.loginService.validateResetToken(this.token).subscribe({
      next: () => this.step = 'form',
      error: () => this.step = 'invalid'
    });
  }

  public get canSubmit(): boolean {
    return this.password.length > 0 && !this.isLoading;
  }

  public onPasswordChange(password: string): void {
    this.password = password;
  }

  public onSubmitClicked(): void {
    if (!this.password) {
      this.alertService.alert('Bitte geben Sie das Passwort zweimal übereinstimmend ein.', AlertTypes.error);
      return;
    }

    this.isLoading = true;
    this.loginService.resetPassword(this.token, this.password).subscribe({
      next: (response: LoginResponse) => {
        this.isLoading = false;
        this.password = '';

        if (this.loginService.isAuthenticatedLoginResponse(response)) {
          this.handleSuccessfulLogin(response);
          return;
        }

        if (this.loginService.isTwoFactorChallenge(response) || this.loginService.isTwoFactorSetupChallenge(response)) {
          this.alertService.alert('Das Passwort wurde gesetzt. Bitte bestätigen Sie jetzt die Zwei-Faktor-Authentifizierung.', AlertTypes.success);
          this.router.navigate(['/login'], { state: { loginResponse: response } });
          return;
        }

        this.alertService.alert('Das Passwort wurde gesetzt. Sie können sich jetzt anmelden.', AlertTypes.success);
        this.router.navigate(['/login']);
      },
      error: (errorContents) => {
        this.isLoading = false;
        // A policy violation (422 PasswordPolicyViolation) is already shown as a toast by HttpService;
        // keep the form open. Any other 422 means the token expired or was used in the meantime.
        const isPolicyViolation = String(errorContents?.code ?? '').toLowerCase() === 'passwordpolicyviolation';
        if (errorContents?.status == 422 && !isPolicyViolation)
          this.step = 'invalid';
        // 429: the password is set, but the account is locked for 2FA attempts right now.
        if (errorContents?.status == 429)
          this.router.navigate(['/login']);
      }
    });
  }

  public onGoToLoginClicked(): void {
    this.router.navigate(['/login']);
  }

  // Same as LoginOverviewComponent.handleSuccessfulLogin: store the JWT, publish the user and go on
  // to the privacy page if the terms have not been accepted yet (always the case for new users).
  private handleSuccessfulLogin(userData: UserDataResponse): void {
    this.jwtTokenService.setJwtToken(userData.jwtToken);
    this.userDataService.updateUserData(userData);
    this.alertService.alert('Das Passwort wurde gesetzt. Sie sind jetzt angemeldet.', AlertTypes.success);

    if (userData.acceptTerms === false) {
      this.router.navigate(['/privacy'], { queryParams: { redirect: '/' } });
      return;
    }

    this.router.navigateByUrl('/');
  }

  public getStepTitle(): string {
    switch (this.step) {
      case 'invalid':
        return 'Link ungültig';
      default:
        return 'Passwort festlegen';
    }
  }

  public getStepDescription(): string {
    switch (this.step) {
      case 'validating':
        return 'Der Link wird geprüft...';
      case 'invalid':
        return 'Der Link ist ungültig, abgelaufen oder wurde bereits verwendet.';
      default:
        return 'Legen Sie Ihr persönliches Passwort für das AVS-Webportal fest.';
    }
  }
}
